import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import {
  Calendar,
  CheckCircle,
  Compass,
  GraduationCap,
  Heart,
  Mail,
  MapPin,
  Phone,
  Send,
  Users,
} from 'lucide-react'
import Button from '../ui/Button'
import { counselingApi } from '../../services/api'
import { DEFAULT_CONTACT } from '../../constants/contact'
import { fadeInUp, viewportOnce } from '../../utils/animations'

const STEPS = [
  { key: 'step1', icon: Users, num: '01' },
  { key: 'step2', icon: Compass, num: '02' },
  { key: 'step3', icon: CheckCircle, num: '03' },
]

const TOPICS = [
  'university_pathways',
  'subject_choices',
  'career_guidance',
  'wellbeing',
  'general',
]

const INITIAL_FORM = {
  parent_name: '',
  email: '',
  phone: '',
  student_name: '',
  current_grade: '',
  program: '',
  campus: '',
  topic: '',
  preferred_date: '',
  preferred_time: '',
  message: '',
}

const inputClass =
  'w-full rounded-xl border border-border bg-background px-4 py-3 text-sm transition-colors focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20'

export default function CounselingForm() {
  const { t } = useTranslation()
  const [form, setForm] = useState(INITIAL_FORM)
  const [status, setStatus] = useState(null)
  const [submitting, setSubmitting] = useState(false)

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSubmitting(true)
    setStatus(null)
    try {
      await counselingApi.submit({
        ...form,
        preferred_date: form.preferred_date || null,
        preferred_time: form.preferred_time || null,
      })
      setStatus('success')
      setForm(INITIAL_FORM)
    } catch {
      setStatus('error')
    } finally {
      setSubmitting(false)
    }
  }

  const grades = t('apply.grades', { returnObjects: true })
  const gradeOptions = Object.entries(grades)

  return (
    <div className="grid gap-10 lg:grid-cols-5 lg:gap-12">
      <motion.aside
        initial="hidden"
        whileInView="visible"
        viewport={viewportOnce}
        variants={fadeInUp}
        className="lg:col-span-2"
      >
        <div className="rounded-3xl gradient-brand p-8 text-white shadow-2xl shadow-primary/30">
          <p className="mb-2 text-sm font-semibold uppercase tracking-widest text-accent">
            {t('counseling.processLabel')}
          </p>
          <h2 className="mb-6 text-2xl font-bold">{t('counseling.processTitle')}</h2>

          <ol className="space-y-6">
            {STEPS.map(({ key, icon: Icon, num }) => (
              <li key={key} className="flex gap-4">
                <div className="relative flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white/15">
                  <Icon className="h-5 w-5" aria-hidden />
                  <span className="absolute -top-1.5 -end-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-accent text-[10px] font-bold">
                    {num}
                  </span>
                </div>
                <div>
                  <h3 className="font-semibold">{t(`counseling.${key}`)}</h3>
                  <p className="mt-1 text-sm text-white/75">{t(`counseling.${key}Desc`)}</p>
                </div>
              </li>
            ))}
          </ol>

          <div className="mt-8 space-y-4 border-t border-white/15 pt-6">
            <p className="text-sm font-semibold text-white/90">{t('counseling.supportTitle')}</p>
            <ul className="space-y-2 text-sm text-white/80">
              {TOPICS.map((topic) => (
                <li key={topic} className="flex items-start gap-2">
                  <Heart className="mt-0.5 h-4 w-4 shrink-0 text-accent" aria-hidden />
                  {t(`counseling.topics.${topic}`)}
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-8 space-y-3 border-t border-white/15 pt-6 text-sm">
            <p className="flex items-center gap-2 text-white/85">
              <Phone className="h-4 w-4 shrink-0 text-accent" aria-hidden />
              <a href={`tel:${DEFAULT_CONTACT.phone}`} className="hover:text-accent">
                {DEFAULT_CONTACT.phone}
              </a>
            </p>
            <p className="flex items-center gap-2 text-white/85">
              <Mail className="h-4 w-4 shrink-0 text-accent" aria-hidden />
              <a href={`mailto:${DEFAULT_CONTACT.email}`} className="hover:text-accent">
                {DEFAULT_CONTACT.email}
              </a>
            </p>
            <p className="flex items-start gap-2 text-white/75">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-accent" aria-hidden />
              {DEFAULT_CONTACT.address_en}
            </p>
          </div>
        </div>
      </motion.aside>

      <motion.form
        initial="hidden"
        whileInView="visible"
        viewport={viewportOnce}
        variants={fadeInUp}
        onSubmit={handleSubmit}
        className="lg:col-span-3 rounded-3xl border border-border bg-white p-6 shadow-xl shadow-primary/5 sm:p-8"
      >
        <div className="mb-6 flex items-center gap-3 border-b border-border/60 pb-6">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-accent/10 text-accent">
            <GraduationCap className="h-5 w-5" aria-hidden />
          </div>
          <div>
            <h2 className="text-xl font-bold text-primary">{t('counseling.formTitle')}</h2>
            <p className="text-sm text-text/60">{t('counseling.formSubtitle')}</p>
          </div>
        </div>

        <fieldset className="mb-8">
          <legend className="mb-4 text-sm font-bold uppercase tracking-wider text-primary">
            {t('counseling.sections.contact')}
          </legend>
          <div className="grid gap-5 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className="mb-1.5 block text-sm font-medium text-text/70">
                {t('counseling.fields.parentName')}
              </label>
              <input
                type="text"
                name="parent_name"
                value={form.parent_name}
                onChange={handleChange}
                required
                className={inputClass}
              />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-text/70">
                {t('counseling.fields.email')}
              </label>
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                required
                className={inputClass}
              />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-text/70">
                {t('counseling.fields.phone')}
              </label>
              <input
                type="tel"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                required
                className={inputClass}
              />
            </div>
          </div>
        </fieldset>

        <fieldset className="mb-8">
          <legend className="mb-4 text-sm font-bold uppercase tracking-wider text-primary">
            {t('counseling.sections.student')}
          </legend>
          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-text/70">
                {t('counseling.fields.studentName')}
              </label>
              <input
                type="text"
                name="student_name"
                value={form.student_name}
                onChange={handleChange}
                required
                className={inputClass}
              />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-text/70">
                {t('counseling.fields.currentGrade')}
              </label>
              <select
                name="current_grade"
                value={form.current_grade}
                onChange={handleChange}
                required
                className={inputClass}
              >
                <option value="">{t('counseling.selectGrade')}</option>
                {gradeOptions.map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-text/70">
                {t('counseling.fields.program')}
              </label>
              <select
                name="program"
                value={form.program}
                onChange={handleChange}
                required
                className={inputClass}
              >
                <option value="">{t('counseling.selectProgram')}</option>
                <option value="national">{t('programs.national.title')}</option>
                <option value="british">{t('programs.british.title')}</option>
                <option value="american">{t('programs.american.title')}</option>
                <option value="undecided">{t('counseling.programs.undecided')}</option>
              </select>
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-text/70">
                {t('counseling.fields.campus')}
              </label>
              <select
                name="campus"
                value={form.campus}
                onChange={handleChange}
                required
                className={inputClass}
              >
                <option value="">{t('counseling.selectCampus')}</option>
                <option value="MisTlaAlAli">{t('apply.campuses.tlaAlAli')}</option>
                <option value="MisKhalda">{t('apply.campuses.khalda')}</option>
                <option value="no_preference">{t('counseling.campuses.noPreference')}</option>
              </select>
            </div>
          </div>
        </fieldset>

        <fieldset className="mb-8">
          <legend className="mb-4 text-sm font-bold uppercase tracking-wider text-primary">
            {t('counseling.sections.appointment')}
          </legend>
          <div className="grid gap-5 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className="mb-1.5 block text-sm font-medium text-text/70">
                {t('counseling.fields.topic')}
              </label>
              <select
                name="topic"
                value={form.topic}
                onChange={handleChange}
                required
                className={inputClass}
              >
                <option value="">{t('counseling.selectTopic')}</option>
                {TOPICS.map((topic) => (
                  <option key={topic} value={topic}>
                    {t(`counseling.topics.${topic}`)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1.5 flex items-center gap-2 text-sm font-medium text-text/70">
                <Calendar className="h-4 w-4 text-accent" aria-hidden />
                {t('counseling.fields.preferredDate')}
              </label>
              <input
                type="date"
                name="preferred_date"
                value={form.preferred_date}
                onChange={handleChange}
                min={new Date().toISOString().split('T')[0]}
                className={inputClass}
              />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-text/70">
                {t('counseling.fields.preferredTime')}
              </label>
              <select
                name="preferred_time"
                value={form.preferred_time}
                onChange={handleChange}
                className={inputClass}
              >
                <option value="">{t('counseling.selectTime')}</option>
                <option value="morning">{t('counseling.times.morning')}</option>
                <option value="afternoon">{t('counseling.times.afternoon')}</option>
                <option value="no_preference">{t('counseling.times.noPreference')}</option>
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="mb-1.5 block text-sm font-medium text-text/70">
                {t('counseling.fields.message')}
              </label>
              <textarea
                name="message"
                value={form.message}
                onChange={handleChange}
                rows={4}
                placeholder={t('counseling.fields.messagePlaceholder')}
                className={`${inputClass} resize-none`}
              />
            </div>
          </div>
        </fieldset>

        {status && (
          <p
            className={`mb-4 rounded-xl px-4 py-3 text-sm ${
              status === 'success'
                ? 'bg-green-50 text-green-700'
                : 'bg-red-50 text-red-700'
            }`}
          >
            {t(`counseling.form.${status}`)}
          </p>
        )}

        <Button type="submit" variant="accent" size="lg" disabled={submitting} className="w-full sm:w-auto">
          <Send className="h-4 w-4" />
          {submitting ? t('common.loading') : t('counseling.submit')}
        </Button>
      </motion.form>
    </div>
  )
}
